/**
 * Objects Tree — layer manager panel listing all drawings on the
 * active chart tab. Toggled from the Layers button in RightSidebar.
 *
 * Features:
 * - Lists all drawings grouped by kind
 * - Click to select / focus a drawing
 * - Eye toggle to show/hide per-drawing
 * - Lock toggle
 * - Delete button per row
 */
import { useEffect } from "react";
import { useAppDispatch, useActiveTabId, useActiveUi } from "@/state/hooks";
import {
  setSelectedDrawing,
  setObjectsTreeOpen,
  removeHorizontalLine,
  removeVerticalLine,
  removeDiagonalLine,
  removePath,
  removeFib,
  removePosition,
  removePriceRangeBox,
  removeTextLabel,
  updateHorizontalLine,
  updateVerticalLine,
  updateDiagonalLine,
  updatePath,
  updateFib,
  updatePosition,
  updatePriceRangeBox,
  updateTextLabel,
  type DrawingKind,
} from "@/state/slices/uiSlice";
import { useAppSelector } from "@/state/hooks";
import styles from "./ObjectsTree.module.css";

// ---- row icons ----

const EyeOnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
    <circle cx="8" cy="8" r="2"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 2l12 12M6.5 6.6A2 2 0 0010 9.5"/>
    <path d="M8 3C4.5 3 2 8 2 8s.8 1.4 2.3 2.7M8 13c3.5 0 6-5 6-5s-.8-1.5-2.4-2.8"/>
  </svg>
);

const LockClosedIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="7.5" width="10" height="7" rx="1.5"/>
    <path d="M5.5 7.5V5a2.5 2.5 0 015 0v2.5"/>
  </svg>
);

const LockOpenIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="7.5" width="10" height="7" rx="1.5"/>
    <path d="M5.5 7.5V5a2.5 2.5 0 014.5-1.5"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.5 4h11M6 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4"/>
    <path d="M4 4l.7 8.5a1 1 0 001 .95h4.6a1 1 0 001-.95L12 4"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
    <path d="M2 2l10 10M12 2L2 12"/>
  </svg>
);

const LayersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3 3 8l9 5 9-5-9-5z" />
    <path d="m3 16 9 5 9-5M3 12l9 5 9-5" />
  </svg>
);

// ---- kind labels ----

const KIND_LABELS: Record<DrawingKind, string> = {
  fib: "Fibonacci",
  hline: "Horizontal lines",
  vline: "Vertical lines",
  diagonal: "Trend lines",
  path: "Elliott waves",
  position: "Positions",
  priceRange: "Price ranges",
  text: "Text labels",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DrawingEntry = { id: string; label: string; kind: DrawingKind; visible: boolean; locked: boolean; raw: any };

export function ObjectsTree() {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const ui = useActiveUi();
  const objectsTreeOpen = useAppSelector((s) => s.ui.objectsTreeOpen);
  const selectedDrawing = ui.selectedDrawing;

  // Close the drawer on Escape — mirrors TradeJournal / Notes.
  useEffect(() => {
    if (!objectsTreeOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setObjectsTreeOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [objectsTreeOpen, dispatch]);

  if (!objectsTreeOpen) return null;

  // Collect all drawings into a flat list, grouped by kind.
  const groups: Record<string, DrawingEntry[]> = {};

  function add<T extends { id: string; visible?: boolean; locked?: boolean }>(
    kind: DrawingKind,
    items: T[],
    labelFn: (d: T) => string,
  ) {
    if (items.length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    groups[kind] = items.map((raw) => ({
      id: raw.id,
      label: labelFn(raw),
      kind,
      visible: raw.visible !== false,
      locked: !!raw.locked,
      raw: raw as any,
    }));
  }

  add("fib", ui.fibRetracements, (f) => `Fib ${f.startPrice.toFixed(2)} → ${f.endPrice.toFixed(2)}`);
  add("hline", ui.horizontalLines, (l) => `@ ${l.price.toFixed(2)}`);
  add("vline", ui.verticalLines, (l) => `@ ${new Date(l.time * 1000).toISOString().slice(0, 10)}`);
  add("diagonal", ui.diagonalLines, (l) => `${l.startPrice.toFixed(2)} → ${l.endPrice.toFixed(2)}`);
  add("path", ui.paths, (p) => `Wave (${p.points.length} pts)`);
  add("position", ui.positions, (p) => `${p.type.toUpperCase()} @ ${p.entryPrice.toFixed(2)}`);
  add("priceRange", ui.priceRangeBoxes, (b) => `Range ${b.startPrice.toFixed(2)} → ${b.endPrice.toFixed(2)}`);
  add("text", ui.textLabels, (l) => `"${l.text}"`);

  const allEntries = Object.values(groups).flat();
  const totalCount = allEntries.length;
  // Lock all while anything is still unlocked; once everything is locked the
  // same button unlocks all.
  const allLocked = totalCount > 0 && allEntries.every((e) => e.locked);

  function setAllLocked(locked: boolean) {
    for (const entry of allEntries) {
      if (entry.locked !== locked) dispatchPatch(dispatch, tabId, entry.kind, entry.id, { locked });
    }
  }

  function toggleVisible(entry: DrawingEntry) {
    const patch = { visible: !entry.visible };
    dispatchPatch(dispatch, tabId, entry.kind, entry.id, patch);
  }

  function toggleLocked(entry: DrawingEntry) {
    const patch = { locked: !entry.locked };
    dispatchPatch(dispatch, tabId, entry.kind, entry.id, patch);
  }

  function deleteEntry(entry: DrawingEntry) {
    dispatchDelete(dispatch, tabId, entry.kind, entry.id);
    if (selectedDrawing?.kind === entry.kind && selectedDrawing.id === entry.id) {
      dispatch(setSelectedDrawing({ tabId, selected: null }));
    }
  }

  function selectEntry(entry: DrawingEntry) {
    dispatch(setSelectedDrawing({ tabId, selected: { kind: entry.kind, id: entry.id } }));
  }

  return (
    <aside className={styles.drawer} aria-label="Objects tree">
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.titleIco}>
            <LayersIcon />
          </span>
          Objects Tree
        </div>
        <span className={`${styles.count} ${totalCount > 0 ? styles.countHas : ""}`}>
          {totalCount}
        </span>
        <button
          type="button"
          className={`${styles.iconBtn} ${allLocked ? styles.iconBtnOn : ""}`}
          onClick={() => setAllLocked(!allLocked)}
          disabled={totalCount === 0}
          title={allLocked ? "Unlock all" : "Lock all"}
          aria-label={allLocked ? "Unlock all drawings" : "Lock all drawings"}
        >
          {allLocked ? <LockClosedIcon /> : <LockOpenIcon />}
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => dispatch(setObjectsTreeOpen(false))}
          title="Close"
          aria-label="Close objects tree"
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.body}>
        {totalCount === 0 ? (
          <div className={styles.empty}>No drawings on this chart.</div>
        ) : (
          Object.entries(groups).map(([kind, entries]) => (
            <div key={kind} className={styles.group}>
              <div className={styles.groupLabel}>
                {KIND_LABELS[kind as DrawingKind]}
                <span className={styles.groupCount}>{entries.length}</span>
              </div>
              {entries.map((entry) => {
                const isSelected = selectedDrawing?.kind === entry.kind && selectedDrawing.id === entry.id;
                return (
                  <div
                    key={entry.id}
                    className={`${styles.row} ${isSelected ? styles.rowSelected : ""} ${!entry.visible ? styles.rowHidden : ""}`}
                    onClick={() => selectEntry(entry)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className={styles.rowLabel} title={entry.label}>{entry.label}</span>
                    <div className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        title={entry.visible ? "Hide" : "Show"}
                        onClick={(e) => { e.stopPropagation(); toggleVisible(entry); }}
                        aria-label={entry.visible ? "Hide" : "Show"}
                      >
                        {entry.visible ? <EyeOnIcon /> : <EyeOffIcon />}
                      </button>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        title={entry.locked ? "Unlock" : "Lock"}
                        onClick={(e) => { e.stopPropagation(); toggleLocked(entry); }}
                        aria-label={entry.locked ? "Unlock" : "Lock"}
                      >
                        {entry.locked ? <LockClosedIcon /> : <LockOpenIcon />}
                      </button>
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); deleteEntry(entry); }}
                        aria-label="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

// ---- dispatch helpers ----

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dispatchPatch(dispatch: ReturnType<typeof useAppDispatch>, tabId: string, kind: DrawingKind, id: string, patch: Record<string, any>) {
  switch (kind) {
    case "hline": dispatch(updateHorizontalLine({ tabId, id, ...patch })); break;
    case "vline": dispatch(updateVerticalLine({ tabId, id, ...patch })); break;
    case "diagonal": dispatch(updateDiagonalLine({ tabId, id, ...patch })); break;
    case "path": dispatch(updatePath({ tabId, id, ...patch })); break;
    case "fib": dispatch(updateFib({ tabId, id, ...patch })); break;
    case "position": dispatch(updatePosition({ tabId, id, ...patch })); break;
    case "priceRange": dispatch(updatePriceRangeBox({ tabId, id, ...patch })); break;
    case "text": dispatch(updateTextLabel({ tabId, id, ...patch })); break;
  }
}

function dispatchDelete(dispatch: ReturnType<typeof useAppDispatch>, tabId: string, kind: DrawingKind, id: string) {
  switch (kind) {
    case "hline": dispatch(removeHorizontalLine({ tabId, id })); break;
    case "vline": dispatch(removeVerticalLine({ tabId, id })); break;
    case "diagonal": dispatch(removeDiagonalLine({ tabId, id })); break;
    case "path": dispatch(removePath({ tabId, id })); break;
    case "fib": dispatch(removeFib({ tabId, id })); break;
    case "position": dispatch(removePosition({ tabId, id })); break;
    case "priceRange": dispatch(removePriceRangeBox({ tabId, id })); break;
    case "text": dispatch(removeTextLabel({ tabId, id })); break;
  }
}
